import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { take } from 'rxjs';
import { DateTime } from 'luxon';
import { APIResponse } from 'fakturnia-shared';
import { SubscriptionsApiService, Order, SubscriptionInvoice } from 'shared/services/api/subscriptions.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { DialogService } from 'shared/services/dialog.service';
import { KsefSubscriptionInvoiceDialogComponent } from 'shared/dialogs/ksef-subscription-invoice-dialog/ksef-subscription-invoice-dialog.component';
import { FulfillmentDialogComponent, FulfillmentDialogData } from '../fulfillment-dialog/fulfillment-dialog.component';

@Component({
  selector: 'app-order-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './order-details-dialog.component.html',
  styleUrls: ['./order-details-dialog.component.scss']
})
export class OrderDetailsDialogComponent implements OnInit {
  isProcessing = false;
  isGeneratingInvoice = false;
  isSendingToKsef = false;
  isLoadingInvoice = false;
  generatedInvoice: SubscriptionInvoice | null = null;

  constructor(
    public dialogRef: MatDialogRef<OrderDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { order: Order },
    private _subscriptionsApi: SubscriptionsApiService,
    private _snackbar: SnackbarService,
    private _dialogService: DialogService,
    private _dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Jeśli zamówienie ma wygenerowaną fakturę, pobierz jej dane
    if (this.order.invoiceGenerated && this.order._id) {
      this._loadInvoice();
    }
  }

  private _loadInvoice() {
    this.isLoadingInvoice = true;
    this._subscriptionsApi.getOrderInvoice(this.order._id!).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isLoadingInvoice = false;
        if (response.success && response.data) {
          this.generatedInvoice = response.data;
        }
      },
      error: () => {
        this.isLoadingInvoice = false;
      }
    });
  }

  get order(): Order {
    return this.data.order;
  }

  formatPrice(priceInCents: number): string {
    return (priceInCents / 100).toFixed(2) + ' zł';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'pending': 'Oczekuje na płatność',
      'paid': 'Opłacone',
      'cancelled': 'Anulowane',
      'refunded': 'Zwrócone'
    };
    return labels[status] || status;
  }

  getStatusClass(status: string): string {
    const classes: Record<string, string> = {
      'pending': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'paid': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'cancelled': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'refunded': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return classes[status] || 'bg-gray-100 text-gray-700';
  }

  getTypeLabel(stage: number): string {
    return String(stage);
  }

  getTypeColor(stage: number): string {
    const colors: Record<number, string> = {
      0: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      1: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      2: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      3: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  }

  getPaymentMethodLabel(): string {
    return this.order.paymentMethod === 'transfer' ? 'Przelew tradycyjny' : 'Przelewy24';
  }

  getDurationLabel(): string {
    const months = this.order.durationMonths || 12;
    if (months === 1) return '1 miesiąc';
    if (months < 5) return `${months} miesiące`;
    return `${months} miesięcy`;
  }

  hasDiscount(): boolean {
    return !!(this.order && (this.order as any).discountPercent && (this.order as any).discountPercent > 0);
  }

  // Sprawdza czy zamówienie oczekuje dłużej niż 7 dni
  isOldPending(): boolean {
    if (this.order.status !== 'pending') return false;
    const createdAt = DateTime.fromISO(this.order.createdAt);
    const now = DateTime.now();
    const diffDays = now.diff(createdAt, 'days').days;
    return diffDays > 7;
  }

  getDaysSinceCreation(): number {
    const createdAt = DateTime.fromISO(this.order.createdAt);
    const now = DateTime.now();
    return Math.floor(now.diff(createdAt, 'days').days);
  }

  markAsPaid() {
    if (this.isProcessing) return;

    // Dla przelewu tradycyjnego - pytaj o datę zapłaty tylko jeśli zamówienie nie jest z dzisiaj
    if (this.order.paymentMethod === 'transfer' && !this._isOrderFromToday()) {
      this._askForPaymentDate();
    } else {
      // Dla Przelewy24 lub zamówienia z dzisiaj - data automatyczna (dziś)
      this._processMarkAsPaid();
    }
  }

  private _isOrderFromToday(): boolean {
    const orderDate = DateTime.fromISO(this.order.createdAt).startOf('day');
    const today = DateTime.now().startOf('day');
    return orderDate.equals(today);
  }

  private _askForPaymentDate() {
    // Domyślnie dzisiejsza data
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    
    this._dialogService.confirmDialogWithForm({
      title: 'Data otrzymania zapłaty',
      message: 'Podaj datę otrzymania przelewu na konto bankowe (data zostanie użyta na fakturze):',
      type: 'info',
      inputs: [
        {
          name: 'paidAt',
          type: 'date',
          description: 'Data zapłaty',
          value: today
        }
      ],
      confirmText: 'Potwierdź płatność',
      cancelText: 'Anuluj'
    }).subscribe({
      next: (result) => {
        if (result.confirmed) {
          const paidAt = result.formValue?.paidAt || today;
          this._processMarkAsPaid(paidAt);
        }
      }
    });
  }

  private _processMarkAsPaid(paidAt?: string) {
    const dialogRef = this._dialog.open(FulfillmentDialogComponent, {
      data: {
        orderId: this.order._id!,
        paidAt
      } as FulfillmentDialogData,
      disableClose: true,
      panelClass: 'fakturnia-responsive-dialog'
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result?.action === 'updated') {
        this.dialogRef.close({ action: 'updated' });
      }
    });
  }

  cancelOrder() {
    if (this.isProcessing) return;

    this._dialogService.confirmDialog({
      title: 'Anulowanie zamówienia',
      message: `Czy na pewno chcesz anulować zamówienie #${this.order.orderNumber}? Tej operacji nie można cofnąć.`,
      type: 'danger',
      confirmText: 'Anuluj zamówienie',
      cancelText: 'Nie anuluj'
    }).subscribe({
      next: (confirmed) => {
        if (confirmed !== true) return;

        this.isProcessing = true;

        this._subscriptionsApi.updateOrderStatus(this.order._id!, 'cancelled').pipe(take(1)).subscribe({
          next: (response: APIResponse) => {
            this.isProcessing = false;
            if (response.success) {
              this._snackbar.showSnackBar('Zamówienie zostało anulowane', 'success');
              this.dialogRef.close({ action: 'updated' });
            } else {
              this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
            }
          },
          error: () => {
            this.isProcessing = false;
            this._snackbar.showSnackBar('Nie udało się anulować zamówienia', 'warning');
          }
        });
      }
    });
  }

  generateInvoice() {
    if (this.isGeneratingInvoice) return;

    this._dialogService.confirmDialog({
      title: 'Generowanie faktury',
      message: `Czy na pewno chcesz wygenerować fakturę dla zamówienia #${this.order.orderNumber}? Dane do faktury zostaną zweryfikowane w bazie GUS.`,
      type: 'info',
      confirmText: 'Generuj fakturę',
      cancelText: 'Anuluj'
    }).subscribe({
      next: (confirmed) => {
        if (confirmed !== true) return;

        this.isGeneratingInvoice = true;

        this._subscriptionsApi.generateInvoice(this.order._id!).pipe(take(1)).subscribe({
          next: (response: APIResponse) => {
            this.isGeneratingInvoice = false;
            if (response.success) {
              this.generatedInvoice = response.data.invoice;
              this.order.invoiceGenerated = true;
              this.order.invoiceId = response.data.invoice._id;
              
              if (response.data.gusWarning) {
                this._snackbar.showSnackBar(`Faktura wygenerowana. Uwaga: ${response.data.gusWarning}`, 'warning');
              } else {
                this._snackbar.showSnackBar(response.message || 'Faktura została wygenerowana', 'success');
              }
            } else {
              this._snackbar.showSnackBar(response.message || 'Nie udało się wygenerować faktury', 'warning');
            }
          },
          error: () => {
            this.isGeneratingInvoice = false;
            this._snackbar.showSnackBar('Wystąpił błąd podczas generowania faktury', 'warning');
          }
        });
      }
    });
  }

  openInvoicePdf() {
    if (this.generatedInvoice?.pdfUrl) {
      window.open(this.generatedInvoice.pdfUrl, '_blank');
    }
  }

  sendToKsef() {
    if (this.isSendingToKsef || !this.generatedInvoice) return;

    this._dialogService.confirmDialog({
      title: 'Wysyłka do KSeF',
      message: `Czy na pewno chcesz wysłać fakturę ${this.generatedInvoice.documentNumber} do KSeF? Faktura zostanie wysłana natychmiastowo.`,
      type: 'info',
      confirmText: 'Wyślij do KSeF',
      cancelText: 'Anuluj'
    }).subscribe({
      next: (confirmed) => {
        if (confirmed !== true) return;

        this.isSendingToKsef = true;

        this._subscriptionsApi.sendInvoiceToKsef(this.generatedInvoice!._id!).pipe(take(1)).subscribe({
          next: (response: APIResponse) => {
            this.isSendingToKsef = false;
            if (response.success) {
              this._snackbar.showSnackBar(`Faktura wysłana do KSeF: ${response.data.ksefNumber}`, 'success');
              // Przeładuj pełne dane faktury i zamknij dialog żeby odświeżyć listing
              this._loadInvoice();
              this.dialogRef.close({ action: 'updated' });
            } else {
              this._snackbar.showSnackBar(response.message || 'Nie udało się wysłać faktury do KSeF', 'warning');
            }
          },
          error: () => {
            this.isSendingToKsef = false;
            this._snackbar.showSnackBar('Wystąpił błąd podczas wysyłki do KSeF', 'warning');
          }
        });
      }
    });
  }

  openKsefDetails() {
    if (!this.generatedInvoice) return;

    const dialogRef = this._dialog.open(KsefSubscriptionInvoiceDialogComponent, {
      data: { invoice: this.generatedInvoice },
      panelClass: 'ksef-details-dialog'
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result?.updated) {
        // Przeładuj dane faktury jeśli zostały zaktualizowane (np. pobrano XML/UPO)
        this._loadInvoice();
      }
    });
  }
}
